var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { i18n } from "@lingui/core";
import { FormControlMixin } from "@open-wc/form-control";
import { html, LitElement, nothing } from "lit";
import { property, query } from "lit/decorators.js";
import { activateI18n } from "../i18n";
import "../link/link.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { reset } from "../styles.js";
import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
import { wButtonStyles } from "./styles.js";
const variants = [
    "primary",
    "secondary",
    "negative",
    "negativeQuiet",
    "utility",
    "pill",
    "link",
    "quiet",
    "utilityQuiet",
    "overlay",
    "overlayInverted",
    "overlayQuiet",
    "overlayInvertedQuiet",
];
/**
 * Performs an action or renders a link with button styling.
 *
 * Use button variants to match action priority, risk, and context.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/button/frameworks/elements)
 */
class WarpButton extends FormControlMixin(LitElement) {
    static { this.styles = [reset, wButtonStyles]; }
    /**
     * Shadow root configuration.
     * Delegates focus from the host to the internal control.
     */
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    // capture the initial value using connectedCallback and #initialValue
    #initialValue;
    updated(changedProperties) {
        if (changedProperties.has("value")) {
            this.setValue(this.value);
        }
    }
    constructor() {
        super();
        /**
         * Focuses the button when it is first rendered.
         * Applies only when the component renders a native button. Link buttons with `href` do not autofocus through this component.
         */
        this.autofocus = false;
        /**
         * Deprecated quiet visual treatment flag
         * Use `variant="quiet"` instead.
         *
         * @deprecated Use `variant="quiet"` instead
         */
        this.quiet = false;
        /**
         * Marks the button as icon-only.
         * Use this when the button has no visible text. Include accessible text in the slotted content so the internal button has a name.
         */
        this.iconOnly = false;
        /**
         * Renders the compact button size.
         * Use this in dense layouts where the default button size is too large.
         */
        this.small = false;
        /**
         * Shows the loading state.
         * Use after the user has triggered an action and the action is in progress.
         */
        this.loading = false;
        /**
         * Visually disables the button.
         * Disabled buttons are discouraged because they can hide the reason an action is unavailable.
         */
        this.disabled = false;
        /**
         * Makes the button fill its parent width.
         * Useful in narrow layouts where the button should span the available inline space.
         */
        this.fullWidth = false;
        // capture the initial value using connectedCallback and #initialValue
        this.#initialValue = undefined;
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
        this.ariaValueTextLoading = i18n.t({
            id: "button.aria.loading",
            message: "Loading...",
            comment: "Screen reader message for buttons that are loading",
        });
    }
    connectedCallback() {
        super.connectedCallback();
        const effectiveVariant = this.variant || "secondary";
        if (!variants.includes(effectiveVariant)) {
            throw new Error(`Invalid "variant" attribute. Set its value to one of the following:\n${variants.join(", ")}.`);
        }
        this.#initialValue = this.value;
    }
    /**
     * Traverse up the shadow roots looking for the ID to support use inside other Lit components.
     */
    closestWithId(id) {
        let root = this.shadowRoot;
        let el;
        try {
            while (root) {
                el = root.getElementById(id);
                if (el)
                    return el;
                root = root.getRootNode().host?.getRootNode();
            }
        }
        catch {
            // Do nothing
        }
        return document.getElementById(id);
    }
    firstUpdated() {
        if (this.autofocus && !this.href) {
            setTimeout(() => this.focus(), 0);
        }
        if (this.buttonEl && this.commandfor) {
            this.buttonEl.commandForElement = this.closestWithId(this.commandfor);
        }
    }
    /** @internal */
    _handleButtonClick() {
        if (this.type === "submit")
            this.internals.form.requestSubmit();
        else if (this.type === "reset")
            this.internals.form.reset();
    }
    resetFormControl() {
        this.value = this.#initialValue;
    }
    render() {
        const variant = this.variant || "secondary";
        return html ` ${this.href
            ? html `
						<w-link
							href=${this.href}
							target=${this.target}
							variant=${this.quiet ? "quiet" : variant}
							?small=${this.small}
							?loading=${this.loading}
							?autofocus=${this.autofocus}
							?full-width=${this.fullWidth}
							class=${this.buttonClass}
							rel=${this.target === "_blank" ? this.rel || "noopener" : undefined}
						>
							<slot></slot>
						</w-link>
					`
            : html `
						<button
							type=${this.type || "button"}
							part="base"
							class=${ifDefined(this.buttonClass)}
							@click="${this._handleButtonClick}"
							commandfor=${ifDefined(this.commandfor)}
							command=${ifDefined(this.command)}
						>
							<slot></slot>
						</button>
					`}
		${this.loading
            ? html `<span
						class="sr-only"
						role="progressbar"
						aria-valuenow="{0}"
						aria-valuetext=${this.ariaValueTextLoading}
					></span>`
            : nothing}`;
    }
}
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "type", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "autofocus", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "quiet", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "icon-only" })
], WarpButton.prototype, "iconOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "small", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "loading", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "href", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "target", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "rel", void 0);
__decorate([
    property({ attribute: "full-width", type: Boolean, reflect: true })
], WarpButton.prototype, "fullWidth", void 0);
__decorate([
    property({ attribute: "button-class", reflect: true })
], WarpButton.prototype, "buttonClass", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "value", void 0);
__decorate([
    property()
], WarpButton.prototype, "commandfor", void 0);
__decorate([
    property()
], WarpButton.prototype, "command", void 0);
__decorate([
    query("button")
    // @ts-expect-error We can't assign to this field because of @query
], WarpButton.prototype, "buttonEl", void 0);
if (!customElements.get("w-button")) {
    customElements.define("w-button", WarpButton);
}
export { WarpButton };
//# sourceMappingURL=button.js.map